// components/SessionComponents.jsx - Updated with DefectTypeAnalysis

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { LoadingSpinner } from './ui/LoadingSpinner.jsx';
import { TrendChart } from './charts/TrendChart.jsx';
import { api, HEATMAP_CONSTANTS } from '../api/reportingApi.js';
import {
    InspectionTable
} from './InspectionComponents.jsx';
// ADD THIS IMPORT
import { DefectTypeAnalysis } from './DefectTypeAnalysis.jsx';

/**
 * Compact session summary card shown in lists.
 */
export const SessionCard = ({ session, onViewDetails, projectName }) => {
    const defectRate = session.total_inspections
        ? ((session.defect_count / session.total_inspections) * 100).toFixed(1)
        : '0.0';

    const humanDuration = (seconds) => {
        if (seconds == null) return 'N/A';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '< 1m';
    };

    const rateNum = parseFloat(defectRate);
    const rateColor = rateNum > 10 ? 'danger' : rateNum > 5 ? 'warning' : 'success';

    // Use projectName prop if provided, otherwise use session.project_name
    const displayProjectName = projectName || session.project_name;

    return (
        <div className="card shadow-sm mb-3 border-0">
            <div className="card-body">
                <div className="row align-items-center">
                    <div className="col-md-4">
                        <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-play-circle text-primary me-2" />
                            <h5 className="card-title mb-0">{displayProjectName}</h5>
                        </div>
                        <p className="text-muted small mb-1">
                            <i className="fas fa-tag me-1" />ID: <code>{session.id}</code>
                        </p>
                        <p className="text-muted small mb-1">
                            <i className="fas fa-clock me-1" />
                            {new Date(session.start_time).toLocaleString()}
                        </p>
                        {session.station_name && (
                            <p className="text-muted small mb-0">
                                <i className="fas fa-industry me-1" />
                                {session.station_name}
                            </p>
                        )}
                    </div>

                    <div className="col-md-5">
                        <div className="row text-center">
                            <div className="col-4 border-end">
                                <h6 className="mb-0 text-primary">{session.total_inspections ?? 0}</h6>
                                <small className="text-muted">Inspections</small>
                            </div>
                            <div className="col-4 border-end">
                                <h6 className={`mb-0 text-${rateColor}`}>{defectRate}%</h6>
                                <small className="text-muted">Defect Rate</small>
                            </div>
                            <div className="col-4">
                                <h6 className="mb-0 text-info">{humanDuration(session.duration_seconds)}</h6>
                                <small className="text-muted">Duration</small>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-3 text-end d-flex flex-column gap-2">
                        <button className="btn btn-primary btn-sm" onClick={() => onViewDetails(session)}>
                            <i className="fas fa-info-circle me-1" />View Details
                        </button>
                        <span className={`badge bg-${session.status === 'active' ? 'success' : 'secondary'}`}>
                            <i className={`fas fa-${session.status === 'active' ? 'play' : 'stop'} me-1`} />
                            {session.status}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Simple heatmap aggregation component for sessions
 */
const SessionHeatmapAggregation = ({ session, sessionInspections, addNotification }) => {
    const [availableModels, setAvailableModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [aggregationMethod, setAggregationMethod] = useState('average');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);

    const sessionId = session?.id || session?._id;

    useEffect(() => {
        if (sessionId) {
            loadAvailableModels();
        }
    }, [sessionId]);

    const loadAvailableModels = async () => {
        try {
            const filters = { session_id: sessionId };
            const models = await api.getAvailableHeatmapModels(filters);
            setAvailableModels(models || []);
            if (models && models.length > 0) {
                setSelectedModel(models[0]);
            }
        } catch (error) {
            console.error('Error loading models:', error);
        }
    };

    const handleGenerateHeatmap = async () => {
        if (!selectedModel) return;

        try {
            setIsLoading(true);
            const filters = { session_id: sessionId };
            const heatmapResult = await api.generateAggregatedHeatmap(
                selectedModel,
                aggregationMethod,
                { useCache: true },
                filters
            );
            setResult(heatmapResult);
            addNotification({type: 'success', message: `Generated heatmap from ${heatmapResult.statistics.total_inspections} inspections`});
        } catch (error) {
            console.error('Error generating heatmap:', error);
            addNotification({type: 'danger', message: `Failed to generate heatmap: ${error.message}`});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!result?.aggregated_heatmap) return;
        try {
            const link = document.createElement('a');
            link.href = result.aggregated_heatmap;
            link.download = `session_${sessionId}_${selectedModel}_${aggregationMethod}_heatmap.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            addNotification({type: 'success', message: 'Heatmap downloaded successfully'});
        } catch (error) {
            console.error("Download failed", error);
            addNotification({type: 'danger', message: `Failed to download heatmap: ${error.message}`});
        }
    };

    if (availableModels.length === 0) {
        return (
            <div className="alert alert-info">
                <i className="fas fa-info-circle me-2" />
                No heatmap data available for this session.
            </div>
        );
    }

    return (
        <div className="card mt-3">
            <div className="card-header">
                <h6 className="mb-0">
                    <i className="fas fa-fire me-2" />
                    Generate Session Heatmap
                </h6>
            </div>
            <div className="card-body">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Model</label>
                        <select 
                            className="form-select" 
                            value={selectedModel} 
                            onChange={(e) => setSelectedModel(e.target.value)}
                            disabled={isLoading}
                        >
                            {availableModels.map(model => (
                                <option key={model} value={model}>{model}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Method</label>
                        <select 
                            className="form-select" 
                            value={aggregationMethod}
                            onChange={(e) => setAggregationMethod(e.target.value)}
                            disabled={isLoading}
                        >
                            {HEATMAP_CONSTANTS.AGGREGATION_METHODS.map(method => (
                                <option key={method.value} value={method.value}>
                                    {method.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="d-flex gap-2 mb-3">
                    <button 
                        className="btn btn-primary"
                        onClick={handleGenerateHeatmap}
                        disabled={!selectedModel || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-fire me-2" />
                                Generate Heatmap
                            </>
                        )}
                    </button>

                    {result && (
                        <button className="btn btn-outline-success" onClick={handleDownload}>
                            <i className="fas fa-download me-2" />
                            Download
                        </button>
                    )}
                </div>

                {result && (
                    <div className="border rounded p-3 bg-light">
                        <div className="text-center mb-3">
                            <img 
                                src={result.aggregated_heatmap} 
                                alt="Session Heatmap"
                                className="img-fluid rounded"
                                style={{ maxHeight: '300px' }}
                            />
                        </div>
                        <div className="text-center">
                            <small className="text-muted">
                                Aggregated from {result.statistics.total_inspections} inspections using {result.statistics.aggregation_method}
                            </small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Large modal showing charts and a paginated list of inspections for a session.
 */
export const SessionDetailsModal = ({
    session,
    isOpen,
    onClose,
    trendData,
    isTrendLoading,
    sessionInspections,
    isInspectionsLoading,
    onViewInspectionDetails,
    onImageClick,
    onSetGroundTruth,
    isGroundTruthLoading,
    inspectionPagination,
    onInspectionPageChange,
    addNotification,
    showTrendChart = true, // Allow hiding trend chart for simpler modals
    modelList
}) => {
    const { state } = useAppContext();

    if (!isOpen || !session) return null;

    const sessionId = session.id || session._id;
    
    // DEFINE FILTERS FOR DEFECT ANALYSIS
    const analysisFilters = { session_id: sessionId, project: session.project_name, date_range: 'all' };

    const defectRate = session.total_inspections
        ? ((session.defect_count / session.total_inspections) * 100).toFixed(1)
        : '0.0';

    const humanDuration = (seconds) => {
        if (seconds == null) return 'N/A';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return [h && `${h}h`, m && `${m}m`, s && `${s}s`].filter(Boolean).join(' ') || '< 1s';
    };

    return (
        <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    {/* Header */}
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-play-circle me-2" />
                            Session Details: {session.project_name || sessionId}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    {/* Body */}
                    <div className="modal-body">
                        {/* Configuration Status Display */}
                        <div className="alert alert-info py-2 mb-4">
                            <i className="fas fa-cog me-2"></i>
                            <small>
                                {state.preferences?.show_confidence_scores !== undefined && 
                                    `Configuration: Confidence scores are ${state.preferences.show_confidence_scores ? 'shown' : 'hidden'}`
                                }
                            </small>
                        </div>

                        {/* Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-lg-6 mb-3 mb-lg-0">
                                <div className="card border-0 bg-light h-100">
                                    <div className="card-header"><h6 className="mb-0">Session Information</h6></div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-6">
                                                <p className="mb-1 fw-bold">Session ID</p>
                                                <p className="mb-2"><code>{sessionId || 'N/A'}</code></p>
                                                <p className="mb-1 fw-bold">Part</p>
                                                <p className="mb-2">{session.project_name}</p>
                                                <p className="mb-1 fw-bold">Station</p>
                                                <p className="mb-0">{session.station_name || 'N/A'}</p>
                                            </div>
                                            <div className="col-6">
                                                <p className="mb-1 fw-bold">Status</p>
                                                <p className="mb-2">
                                                    <span className={`badge bg-${session.status === 'active' ? 'success' : 'secondary'}`}>{session.status}</span>
                                                </p>
                                                <p className="mb-1 fw-bold">Duration</p>
                                                <p className="mb-2">{humanDuration(session.duration_seconds)}</p>
                                                <p className="mb-1 fw-bold">Started</p>
                                                <p className="mb-0">{new Date(session.start_time).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="card border-0 bg-light h-100">
                                    <div className="card-header"><h6 className="mb-0">Performance Metrics</h6></div>
                                    <div className="card-body d-flex align-items-center">
                                        <div className="row text-center w-100">
                                            <div className="col-4">
                                                <h4 className="text-primary mb-1">{session.total_inspections ?? 0}</h4>
                                                <small className="text-muted">Total</small>
                                            </div>
                                            <div className="col-4">
                                                <h4 className={`text-${parseFloat(defectRate) > 5 ? 'danger' : 'success'} mb-1`}>{defectRate}%</h4>
                                                <small className="text-muted">Defect Rate</small>
                                            </div>
                                            <div className="col-4">
                                                <h4 className="text-warning mb-1">{session.defect_count ?? 0}</h4>
                                                <small className="text-muted">Defects</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trend Chart - Optional */}
                        {showTrendChart && (
                            <div className="card mb-4">
                                <div className="card-header"><h6 className="mb-0"><i className="fas fa-chart-line text-primary me-2" />Performance Trend</h6></div>
                                <div className="card-body">
                                    {isTrendLoading ? (
                                        <LoadingSpinner message="Loading trend data..." />
                                    ) : trendData && trendData.length > 0 ? (
                                        <div style={{ height: 300 }}>
                                            <TrendChart chartId={`session-trend-${sessionId}`} data={trendData} />
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted p-4">
                                            <i className="fas fa-chart-line fa-3x mb-3" />
                                            <h6>No Trend Data Available</h6>
                                            <p className="small">Trend requires multiple inspections over time.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ADD DEFECT TYPE ANALYSIS COMPONENT */}
                        {sessionInspections && sessionInspections.length > 0 && (
                            <div className="mb-4">
                                <DefectTypeAnalysis 
                                    filters={analysisFilters}
                                    title={`Defect Type Analysis for this Session`}
                                    addNotification={addNotification}
                                    compactMode={false}
                                    modelList={modelList}
                                />
                            </div>
                        )}

                        {/* Simple Heatmap Aggregation */}
                        <SessionHeatmapAggregation 
                            session={session}
                            sessionInspections={sessionInspections}
                            addNotification={addNotification}
                        />

                        {/* Inspection Table */}
                        {isInspectionsLoading && sessionInspections.length === 0 ? (
                            <LoadingSpinner message="Loading session inspections..." />
                        ) : (
                            <InspectionTable
                                inspections={sessionInspections}
                                onViewDetails={onViewInspectionDetails}
                                onImageClick={onImageClick}
                                onSetGroundTruth={onSetGroundTruth}
                                isLoading={isGroundTruthLoading || isInspectionsLoading}
                                showExportButtons={false}
                                title={`Inspections for Session: ${sessionId}`}
                                addNotification={addNotification}
                                filters={analysisFilters}
                            />
                        )}

                        {/* Pagination for Inspections */}
                        {inspectionPagination.totalPages > 1 && (
                            <div className="card mt-3 border-0 bg-light">
                                <div className="card-body py-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="small text-muted">
                                            Page {inspectionPagination.page} of {inspectionPagination.totalPages} ({inspectionPagination.total.toLocaleString()} total)
                                        </div>
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">
                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => onInspectionPageChange(1)}>«</button>
                                                </li>
                                                <li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.page - 1)}>‹</button>
                                                </li>
                                                <li className="page-item active"><span className="page-link">{inspectionPagination.page}</span></li>
                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.page + 1)}>›</button>
                                                </li>
                                                <li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}>
                                                    <button className="page-link" onClick={() => onInspectionPageChange(inspectionPagination.totalPages)}>»</button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="fas fa-times me-2" />Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};